import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Search, X, AlertTriangle, ArrowRightLeft } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import BeachMap from "@/components/BeachMap";
import StatusLegend from "@/components/StatusLegend";
import RefreshBar from "@/components/RefreshBar";
import StatusPill from "@/components/StatusPill";
import { InlineLoader } from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "@/components/ui/drawer";
import { STATUS_FILTERS, statusMeta, initials } from "@/lib/status";

export default function WorkerMap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback((spin = false) => {
    if (spin) setLoading(true);
    api
      .get("/map")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(true); }, [load]);

  const baldachins = data?.baldachins || [];
  const q = search.trim();
  const matches = useCallback(
    (b) => {
      if (filter !== "all" && b.status !== filter) return false;
      if (q && !String(b.number).includes(q)) return false;
      return true;
    },
    [filter, q]
  );
  const anyFilter = filter !== "all" || q.length > 0;
  const dimIds = useMemo(
    () => (anyFilter ? new Set(baldachins.filter((b) => !matches(b)).map((b) => b.id)) : null),
    [anyFilter, baldachins, matches]
  );
  const visibleCount = baldachins.filter(matches).length;

  const confirmSell = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/baldachins/${selected.id}/sell`);
      toast.success(r.data.action_type === "sold" ? `Baldachin ${selected.number} marked as Sold` : `Baldachin ${selected.number} sold again (${r.data.count}x)`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !data) return <InlineLoader label="Loading the beach map…" />;

  const isFree = selected?.status === "free";
  const meta = selected ? statusMeta(selected.status) : null;

  return (
    <div className="space-y-4 bb-fade-up">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight">Beach Map</h1>
        <RefreshBar lastUpdated={data?.last_updated} onRefresh={() => load(true)} loading={loading} testid="worker-refresh-btn" />
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-400" />
        <Input
          data-testid="worker-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search baldachin number…"
          inputMode="numeric"
          className="h-12 rounded-xl pl-10 text-base"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" data-testid="worker-clear-search">
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <div className="bb-scroll -mx-1 flex gap-2 overflow-x-auto px-1">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`worker-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`whitespace-nowrap rounded-xl px-4 py-2 text-sm font-bold transition ${
              filter === f.key ? "bg-primary text-white shadow-sm" : "bg-white text-stone-600 border border-stone-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between">
        <StatusLegend />
        <span className="text-xs font-semibold text-stone-500" data-testid="worker-visible-count">
          {anyFilter ? `${visibleCount} shown` : `${baldachins.length} baldachins`}
        </span>
      </div>

      {anyFilter && visibleCount === 0 && (
        <div className="flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm font-medium text-amber-700" data-testid="worker-no-match">
          <AlertTriangle className="h-4 w-4" /> No baldachins match your search.
        </div>
      )}

      <BeachMap
        baldachins={baldachins}
        mode="worker"
        dimIds={dimIds}
        onCardClick={(b) => setSelected(b)}
        minHeight={520}
        initialZoom={0.9}
      />

      <Drawer open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DrawerContent data-testid="worker-action-drawer">
          {selected && (
            <div className="mx-auto w-full max-w-md">
              <DrawerHeader className="text-center">
                <div
                  className="mx-auto mb-2 flex h-16 w-16 items-center justify-center rounded-2xl font-heading text-3xl font-black"
                  style={{ background: meta.bg, color: meta.text, border: `2px solid ${meta.border}` }}
                >
                  {selected.number}
                </div>
                <DrawerTitle className="font-heading text-2xl">Baldachin {selected.number}</DrawerTitle>
                <div className="mt-1 flex justify-center"><StatusPill status={selected.status} /></div>
              </DrawerHeader>

              {!isFree && (
                <div className="mx-4 mb-2 space-y-2 rounded-xl bg-stone-50 p-4 text-sm">
                  <Row label="Sold today" value={`${selected.count}x`} />
                  <Row label="Last sold by" value={selected.last_worker_name ? `${selected.last_worker_name} (${initials(selected.last_worker_name)})` : "—"} />
                  <Row label="Last sold at" value={selected.last_action_time_label || "—"} />
                </div>
              )}

              <DrawerFooter>
                {isFree ? (
                  <>
                    <p className="text-center text-xs text-stone-500">This action cannot be undone by you. Only an admin can reopen a baldachin.</p>
                    <Button
                      onClick={confirmSell}
                      disabled={submitting}
                      data-testid="confirm-mark-sold"
                      className="h-14 w-full rounded-2xl text-lg font-bold"
                    >
                      Mark as Sold
                    </Button>
                  </>
                ) : (
                  <>
                    <p className="flex items-center justify-center gap-1.5 text-center text-xs text-stone-500">
                      <ArrowRightLeft className="h-3.5 w-3.5" /> Use this only if the previous guest left and the baldachin was rented again.
                    </p>
                    <Button
                      onClick={confirmSell}
                      disabled={submitting}
                      data-testid="confirm-mark-sold-again"
                      className="h-14 w-full rounded-2xl text-lg font-bold"
                      style={{ background: "#EA580C" }}
                    >
                      Mark as Sold Again
                    </Button>
                  </>
                )}
                <Button variant="ghost" onClick={() => setSelected(null)} data-testid="cancel-worker-action" className="h-12 rounded-xl text-base font-semibold">
                  Cancel
                </Button>
              </DrawerFooter>
            </div>
          )}
        </DrawerContent>
      </Drawer>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-stone-500">{label}</span>
      <span className="font-bold">{value}</span>
    </div>
  );
}
